import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiDownload, FiCalendar, FiClock } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getLearningSpeedV2Data, LearningSpeedUser2, LearningSpeedResponse2 } from '../../api/admin';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get Monday of current week
const getMonday = (date: Date): Date => {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(date.setDate(diff));
};

// Access denied component
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const LearningSpeedHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Learning Speed Dashboard v2
    </span>
  </h1>
);

// Controls component
interface ControlsProps {
  timePeriod: 'all' | 'week';
  onTimePeriodChange: (period: 'all' | 'week') => void;
  ageGradeFilter: number | null;
  onAgeGradeFilterChange: (grade: number | null) => void;
  knowledgeGradeFilter: number | null;
  onKnowledgeGradeFilterChange: (grade: number | null) => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
  dateRange: { startDate: string; endDate: string };
}

const Controls: React.FC<ControlsProps> = ({
  timePeriod,
  onTimePeriodChange,
  ageGradeFilter,
  onAgeGradeFilterChange,
  knowledgeGradeFilter,
  onKnowledgeGradeFilterChange,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false,
  dateRange
}) => {
  const gradeOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Time Period Toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Time Period
            </label>
            <div className="flex gap-3">
              <Button
                onClick={() => onTimePeriodChange('all')}
                disabled={isLoading}
                variant={timePeriod === 'all' ? 'default' : 'outline'}
                className={`flex-1 h-20 flex flex-col items-center justify-center gap-2 transition-all ${
                  timePeriod === 'all'
                    ? 'shadow-md ring-2 ring-blue-500 ring-offset-2'
                    : 'hover:bg-gray-50'
                }`}
              >
                <FiCalendar className="w-5 h-5" />
                <div className="flex flex-col items-center">
                  <span className="font-semibold">All Time</span>
                  <span className="text-xs opacity-80">Since Sept 1, 2025</span>
                </div>
              </Button>
              <Button
                onClick={() => onTimePeriodChange('week')}
                disabled={isLoading}
                variant={timePeriod === 'week' ? 'default' : 'outline'}
                className={`flex-1 h-20 flex flex-col items-center justify-center gap-2 transition-all ${
                  timePeriod === 'week'
                    ? 'shadow-md ring-2 ring-blue-500 ring-offset-2'
                    : 'hover:bg-gray-50'
                }`}
              >
                <FiClock className="w-5 h-5" />
                <div className="flex flex-col items-center">
                  <span className="font-semibold">This Week</span>
                  <span className="text-xs opacity-80">Monday - Today</span>
                </div>
              </Button>
            </div>
          </div>

          {/* Grade Filters and Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="ageGradeFilter" className="block text-sm font-medium text-gray-700 mb-2">
                Age Grade Filter
              </label>
              <select
                id="ageGradeFilter"
                value={ageGradeFilter || ''}
                onChange={(e) => onAgeGradeFilterChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              >
                <option value="">All Grades</option>
                {gradeOptions.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="knowledgeGradeFilter" className="block text-sm font-medium text-gray-700 mb-2">
                Knowledge Grade Filter
              </label>
              <select
                id="knowledgeGradeFilter"
                value={knowledgeGradeFilter || ''}
                onChange={(e) => onKnowledgeGradeFilterChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              >
                <option value="">All Grades</option>
                {gradeOptions.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 sm:flex-shrink-0">
              <Button
                onClick={onRefresh}
                disabled={isLoading}
                variant="outline"
                className="flex items-center px-4"
              >
                <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={onDownloadCSV}
                disabled={isLoading || !hasData}
                variant="outline"
                className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                <FiDownload className="w-4 h-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p>📅 Data Range: {dateRange.startDate} - {dateRange.endDate}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalUsers: number;
  statusCounts: {
    onTrack: number;
    needsAttention: number;
    atRisk: number;
    aboveAgeGrade: number;
    allGradesMastered: number;
    noData: number;
  };
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalUsers,
  statusCounts,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100 mb-8">
        <CardContent className="p-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100 mb-8">
      <CardContent className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-blue-600">
              {totalUsers.toLocaleString()}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Total Users
            </p>
            <p className="text-xs text-gray-500">
              All students in the system
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-green-600">
              {statusCounts.onTrack}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              On-track
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.onTrack / totalUsers) * 100).toFixed(1) : '0.0'}% • Both rates ≥ 1.0
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-yellow-600">
              {statusCounts.needsAttention}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Needs attention
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.needsAttention / totalUsers) * 100).toFixed(1) : '0.0'}% • One rate &lt; 1.0
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-red-600">
              {statusCounts.atRisk}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              At risk
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.atRisk / totalUsers) * 100).toFixed(1) : '0.0'}% • Both rates &lt; 0.9
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-purple-600">
              {statusCounts.aboveAgeGrade}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Above age grade
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.aboveAgeGrade / totalUsers) * 100).toFixed(1) : '0.0'}% • Working ahead
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-indigo-600">
              {statusCounts.allGradesMastered}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              All grades mastered
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.allGradesMastered / totalUsers) * 100).toFixed(1) : '0.0'}% • Completed
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-gray-600">
              {statusCounts.noData}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              No data available
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((statusCounts.noData / totalUsers) * 100).toFixed(1) : '0.0'}% • Missing info
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Learning speed table component
interface LearningSpeedTableProps {
  users: LearningSpeedUser2[];
  isLoading?: boolean;
}

const LearningSpeedTable: React.FC<LearningSpeedTableProps> = ({
  users,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No learning speed data found for the selected criteria.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Helper function to get color class for LS values
  const getLSColorClass = (value: number | null): string => {
    if (value === null) return 'bg-gray-100 text-gray-600';
    if (value >= 1.0) return 'bg-green-100 text-green-800';
    if (value >= 0.9) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Knowledge Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Track ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Used
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  XP Earned
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CQPM Growth Rate
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fact Fluency Growth Rate
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user, index) => (
                <tr key={`${user.userEmail}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.userEmail}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.ageGrade !== null ? `Grade ${user.ageGrade}` : 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.knowledgeGrade !== null ? `Grade ${user.knowledgeGrade}` : 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      !user.highestTrackId || user.highestTrackId === 'ALL'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {!user.highestTrackId ? 'Missing' : user.highestTrackId}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isSpendingEnoughTime
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.allocatedTimeHours > 0
                        ? `${Math.round((user.totalTimeHours / user.allocatedTimeHours) * 100)}% (${user.totalTimeHours.toFixed(1)} / ${user.allocatedTimeHours.toFixed(1)} hrs)`
                        : `${user.totalTimeHours.toFixed(1)} hrs`
                      }
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isSpendingEnoughActiveTime
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.allocatedTimeHours > 0
                        ? `${Math.round((user.activeTimeHours / user.allocatedTimeHours) * 100)}% (${user.activeTimeHours.toFixed(1)} / ${user.allocatedTimeHours.toFixed(1)} hrs)`
                        : `${user.activeTimeHours.toFixed(1)} hrs`
                      }
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isSpendingEnoughXP
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.allocatedXP > 0
                        ? `${Math.round((user.xpTotal / user.allocatedXP) * 100)}% (${user.xpTotal.toFixed(1)} / ${user.allocatedXP.toFixed(1)} XP)`
                        : `${user.xpTotal.toFixed(1)} XP`
                      }
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLSColorClass(user.ls1)}`}
                      title="CQPM Growth Rate: [(highest-first)/(target-first)] / (time/allocated)">
                      {user.ls1 !== null ? user.ls1.toFixed(2) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLSColorClass(user.ls2)}`}
                      title="Fact Fluency Growth Rate: (Facts Mastered / Total) / (Time / Allocated)">
                      {user.ls2 !== null ? user.ls2.toFixed(2) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.status === 'On-track'
                        ? 'bg-green-100 text-green-800'
                        : user.status === 'Needs attention'
                        ? 'bg-yellow-100 text-yellow-800'
                        : user.status === 'At risk'
                        ? 'bg-red-100 text-red-800'
                        : user.status === 'Above age grade'
                        ? 'bg-purple-100 text-purple-800'
                        : user.status === 'All grades mastered'
                        ? 'bg-indigo-100 text-indigo-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const LearningSpeedDashboard2: React.FC = () => {
  const { user: currentUser } = useAuth();

  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [data, setData] = useState<LearningSpeedResponse2 | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<'all' | 'week'>('all');
  const [ageGradeFilter, setAgeGradeFilter] = useState<number | null>(null);
  const [knowledgeGradeFilter, setKnowledgeGradeFilter] = useState<number | null>(null);

  // Calculate date range based on time period
  const dateRange = useMemo(() => {
    const today = new Date();

    if (timePeriod === 'all') {
      return {
        startDate: '2025-09-01',
        endDate: formatDateLocal(today)
      };
    } else {
      // This week (Monday to today)
      const monday = getMonday(new Date());
      return {
        startDate: formatDateLocal(monday),
        endDate: formatDateLocal(today)
      };
    }
  }, [timePeriod]);

  // Filter data based on age grade and knowledge grade filters
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];

    return data.users.filter(user => {
      const matchesAgeGrade = ageGradeFilter === null || user.ageGrade === ageGradeFilter;
      const matchesKnowledgeGrade = knowledgeGradeFilter === null || user.knowledgeGrade === knowledgeGradeFilter;
      return matchesAgeGrade && matchesKnowledgeGrade;
    });
  }, [data, ageGradeFilter, knowledgeGradeFilter]);

  // Calculate summary stats from filtered data
  const summaryStats = useMemo(() => {
    if (filteredUsers.length === 0) {
      return {
        totalUsers: 0,
        statusCounts: {
          onTrack: 0,
          needsAttention: 0,
          atRisk: 0,
          aboveAgeGrade: 0,
          allGradesMastered: 0,
          noData: 0
        }
      };
    }

    const statusCounts = {
      onTrack: 0,
      needsAttention: 0,
      atRisk: 0,
      aboveAgeGrade: 0,
      allGradesMastered: 0,
      noData: 0
    };

    filteredUsers.forEach(user => {
      if (user.status === 'On-track') {
        statusCounts.onTrack++;
      } else if (user.status === 'Needs attention') {
        statusCounts.needsAttention++;
      } else if (user.status === 'At risk') {
        statusCounts.atRisk++;
      } else if (user.status === 'Above age grade') {
        statusCounts.aboveAgeGrade++;
      } else if (user.status === 'All grades mastered') {
        statusCounts.allGradesMastered++;
      } else {
        // Catch all other statuses: "-", "Error - Age Grade Missing", etc.
        statusCounts.noData++;
      }
    });

    return {
      totalUsers: filteredUsers.length,
      statusCounts
    };
  }, [filteredUsers]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getLearningSpeedV2Data(dateRange.startDate, dateRange.endDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch learning speed v2 data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!filteredUsers || filteredUsers.length === 0) return;

    const headers = [
      { key: 'userEmail' as keyof LearningSpeedUser2, label: 'User Email' },
      { key: 'ageGrade' as keyof LearningSpeedUser2, label: 'Age Grade' },
      { key: 'knowledgeGrade' as keyof LearningSpeedUser2, label: 'Knowledge Grade' },
      { key: 'highestTrackId' as keyof LearningSpeedUser2, label: 'Track ID' },
      { key: 'totalTimeHours' as keyof LearningSpeedUser2, label: 'Total Time (hours)' },
      { key: 'isSpendingEnoughTime' as keyof LearningSpeedUser2, label: 'Time Used - Meets Minimum' },
      { key: 'activeTimeHours' as keyof LearningSpeedUser2, label: 'Active Time (hours)' },
      { key: 'isSpendingEnoughActiveTime' as keyof LearningSpeedUser2, label: 'Active Time - Meets Minimum' },
      { key: 'xpTotal' as keyof LearningSpeedUser2, label: 'XP Earned' },
      { key: 'isSpendingEnoughXP' as keyof LearningSpeedUser2, label: 'XP - Meets Minimum' },
      { key: 'allocatedTimeHours' as keyof LearningSpeedUser2, label: 'Allocated Time (hours)' },
      { key: 'allocatedXP' as keyof LearningSpeedUser2, label: 'Allocated XP' },
      { key: 'targetCQPM' as keyof LearningSpeedUser2, label: 'Target CQPM' },
      { key: 'ls1' as keyof LearningSpeedUser2, label: 'CQPM Growth Rate' },
      { key: 'factsMastered' as keyof LearningSpeedUser2, label: 'Facts Mastered' },
      { key: 'totalFacts' as keyof LearningSpeedUser2, label: 'Total Facts' },
      { key: 'ls2' as keyof LearningSpeedUser2, label: 'Fact Fluency Growth Rate' },
      { key: 'status' as keyof LearningSpeedUser2, label: 'Status' }
    ];

    // Filter out users with status "Error - Age Grade Missing" or "-"
    const validUsers = filteredUsers.filter(user =>
      user.status !== 'Error - Age Grade Missing' && user.status !== '-'
    );

    const csvData = validUsers.map(user => ({
      ...user,
      ageGrade: user.ageGrade !== null ? user.ageGrade : 'N/A',
      knowledgeGrade: user.knowledgeGrade !== null ? user.knowledgeGrade : 'N/A',
      highestTrackId: user.highestTrackId || 'Missing',
    }));

    const csvContent = convertToCSV(csvData, headers);
    const periodSuffix = timePeriod === 'week' ? '-this-week' : '-all-time';
    const ageGradeFilterSuffix = ageGradeFilter ? `-age${ageGradeFilter}` : '';
    const knowledgeGradeFilterSuffix = knowledgeGradeFilter ? `-knowledge${knowledgeGradeFilter}` : '';
    const filename = `learning-speed-v2${periodSuffix}${ageGradeFilterSuffix}${knowledgeGradeFilterSuffix}-${formatDateForFilename()}.csv`;

    downloadCSV(csvContent, filename);
  }, [filteredUsers, ageGradeFilter, knowledgeGradeFilter, timePeriod]);

  // Load data on component mount and when date range changes
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <LearningSpeedHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Controls */}
        <Controls
          timePeriod={timePeriod}
          onTimePeriodChange={setTimePeriod}
          ageGradeFilter={ageGradeFilter}
          onAgeGradeFilterChange={setAgeGradeFilter}
          knowledgeGradeFilter={knowledgeGradeFilter}
          onKnowledgeGradeFilterChange={setKnowledgeGradeFilter}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={filteredUsers.length > 0}
          dateRange={dateRange}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalUsers={summaryStats.totalUsers}
              statusCounts={summaryStats.statusCounts}
              isLoading={isLoading}
            />

            {/* Learning Speed Table */}
            <LearningSpeedTable
              key={`table-${timePeriod}-age${ageGradeFilter || 'all'}-knowledge${knowledgeGradeFilter || 'all'}-${filteredUsers.length}`}
              users={filteredUsers}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};
